import React, { useState, useEffect } from 'react';
import { useTheme } from '@mui/material';
import { useThemeContext } from '../pages/context/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

import {
  Box, Typography, IconButton, Avatar, Button, Stack
} from '@mui/material';
import {
  Menu as MenuIcon,
  NotificationsNone as NotificationsIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  ListAlt as ListAltIcon,
  AccountBalanceWallet as WalletIcon,
  Brightness2 as DarkIcon,
  LightMode as LightIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

function SidebarIcon({ icon, label, to, active, onClick, tooltip }) {
  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      title={tooltip}
      style={{
        marginBottom: 16,
        cursor: onClick || to ? 'pointer' : 'default',
      }}
      onClick={onClick}
    >
      <Button
        component={to ? RouterLink : 'button'}
        to={to}
        startIcon={icon}
        fullWidth
        sx={{
          justifyContent: 'flex-start',
          bgcolor: active ? '#232e41' : 'transparent',
          color: active ? '#5C6AC4' : '#cbd5e1',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8rem',
          px: 2,
          py: 1,
          borderRadius: 2,
          '&:hover': {
            bgcolor: '#232e41',
            color: '#9B51E0',
          },
        }}
      >
        {label}
      </Button>
    </motion.div>
  );
}

function MobileMenu({ open, onClose, onLogout }) {
  const { mode, toggleTheme } = useThemeContext();

  return (
    <motion.div
      initial={{ x: '-100%' }}
      animate={{ x: open ? 0 : '-100%' }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: 220,
        height: '100vh',
        background: '#0D0F2B',
        zIndex: 2000,
        boxShadow: '2px 0 16px #0008',
        display: open ? 'block' : 'none',
      }}
    >
      <Box p={3}>
        <Typography fontWeight="bold" color="#fff" fontSize={22}>🌐</Typography>
        <Typography variant="caption" color="#5C6AC4" fontWeight="bold" ml={2}>
          FENTRIXBANK
        </Typography>

        <Stack spacing={2} mt={3}>
          <SidebarIcon icon={<HomeIcon />} label="Home" to="/user/dashboard" />
          <SidebarIcon icon={<PersonIcon />} label="Profile" to="/account-settings" />
          <SidebarIcon icon={<ListAltIcon />} label="Transactions" to="/transactions" />
          <SidebarIcon icon={<WalletIcon />} label="Kyc" to="/kyc-verification" />
          <SidebarIcon
            icon={mode === 'dark' ? <LightIcon /> : <DarkIcon />}
            label={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
            onClick={toggleTheme}
            tooltip={`Switch to ${mode === 'dark' ? 'Light' : 'Dark'} Mode`}
          />
          <SidebarIcon icon={<LogoutIcon />} label="Logout" onClick={onLogout} tooltip="Logout" />
        </Stack>

        <Button
          onClick={onClose}
          sx={{
            mt: 4,
            color: '#fff',
            borderColor: '#fff',
            borderRadius: 2,
            border: '1px solid #fff',
            width: '100%',
          }}
        >
          Close
        </Button>
      </Box>
    </motion.div>
  );
}

const Header = () => {
  const theme = useTheme();
  const { mode } = useThemeContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 64,
          px: 2,
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          boxShadow: mode === 'dark'
            ? '0 1px 4px rgba(255,255,255,0.05)'
            : '0 2px 8px rgba(0,0,0,0.05)',
          zIndex: 1000,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <motion.div whileTap={{ scale: 0.8, rotate: 90 }}>
            <IconButton onClick={() => setMenuOpen(!menuOpen)}>
              <MenuIcon sx={{ color: theme.palette.text.primary }} />
            </IconButton>
          </motion.div>
          <Typography variant="h6" fontWeight="bold">
            FentrixBank
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <IconButton>
            <NotificationsIcon sx={{ color: theme.palette.text.primary }} />
          </IconButton>
          <Avatar sx={{ bgcolor: '#7B2FF2', width: 35, height: 35 }}>
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
        </Box>
      </Box>

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} onLogout={logout} />
    </>
  );
};

export default Header;
export { SidebarIcon, MobileMenu, Header };